import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRunningNote, useSaveRunningNote } from "@/hooks/useRunningNote";
import { RUNNING_NOTE_MAX_CHARS } from "@/services/plannerNotesService";
import { roundTripNoteMarkdown } from "@/lib/noteMarkdown";
import { useNoteUploads } from "@/hooks/useNoteFiles";
import { NoteAttachments } from "./NoteAttachments";
import { NoteEditor } from "./NoteEditor";
import { RichNoteText } from "./RichNoteText";

/**
 * The Planner's "Notes" card: one running internal note per client, the same on
 * every month, kept on the Planner's own row (O49). Sits between the scoreboard
 * and the task list.
 */
export function RunningNotesCard({ tenantId }: { tenantId?: string | null }) {
  const { data, isLoading, isError, refetch } = useRunningNote(tenantId);
  const save = useSaveRunningNote(tenantId);
  const [open, setOpen] = useState(false);
  const note = data?.note ?? "";
  // What the editor would write for the note as it stands: a note it cannot
  // reproduce byte for byte (old-style bold, `* ` bullets) must not enable Save
  // on open, so only a real edit differs from this.
  const baseline = roundTripNoteMarkdown(note);
  const [text, setText] = useState(baseline);

  useEffect(() => {
    if (open) setText(roundTripNoteMarkdown(note));
    // Reset the draft only when the editor opens, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const unchanged = text === baseline;
  const linked = data?.available !== false;
  const attachmentsEnabled = data?.attachments_enabled === true;
  // Above the dialog so an upload keeps going (and shows on the card) if the dialog closes.
  const uploads = useNoteUploads(tenantId, attachmentsEnabled && linked);

  const onSave = () => {
    save.mutate(text, { onSuccess: () => setOpen(false) });
  };

  return (
    <Card className="border-0 shadow-sm">
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <div>
          <h2 className="text-lg font-bold text-foreground">Notes</h2>
          <p className="text-xs text-muted-foreground">
            A running list of internal reminders for this client. Stays the same across every month.
          </p>
        </div>
        {data && linked ? (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            {note ? "Edit notes" : "Add notes"}
          </Button>
        ) : null}
      </div>

      <div className="border-t border-border px-4 py-3 text-sm">
        {isLoading ? (
          <div className="space-y-2" aria-busy="true" aria-label="Loading notes">
            <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
          </div>
        ) : isError ? (
          <p className="text-muted-foreground">
            Couldn't load the notes.{" "}
            <button type="button" className="text-link hover:underline" onClick={() => refetch()}>
              Try again
            </button>
          </p>
        ) : !linked ? (
          <p className="text-muted-foreground">
            This client is not linked to the Planner yet, so notes are not available.
          </p>
        ) : note ? (
          <div className="max-h-28 overflow-y-auto text-foreground">
            <RichNoteText text={note} />
          </div>
        ) : (
          <p className="text-muted-foreground">No notes yet</p>
        )}
      </div>

      {data && linked && attachmentsEnabled ? (
        <div className="border-t border-border px-4 py-3">
          <NoteAttachments tenantId={tenantId} enabled uploads={uploads} />
        </div>
      ) : null}

      <Dialog open={open} onOpenChange={(next) => (save.isPending ? undefined : setOpen(next))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{note ? "Edit notes" : "Add notes"}</DialogTitle>
          </DialogHeader>
          <NoteEditor
            initialMarkdown={note}
            onChange={setText}
            onFiles={uploads.upload}
            attachmentsEnabled={attachmentsEnabled}
            uploading={uploads.uploading}
          />
          <NoteAttachments tenantId={tenantId} enabled={attachmentsEnabled} uploads={uploads} />
          <DialogFooter className="items-center">
            <span className="mr-auto text-xs tabular-nums text-muted-foreground">
              {text.length} / {RUNNING_NOTE_MAX_CHARS}
            </span>
            <Button variant="outline" size="sm" disabled={save.isPending} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={unchanged || save.isPending} onClick={onSave}>
              {save.isPending ? "Saving…" : "Save notes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default RunningNotesCard;
